import { useQueryClient } from "@tanstack/react-query";
import React, { useMemo } from "react";
import styled from "styled-components";

import { reactKeyFromIDSK } from "@examspark/common";
import {
  getFullAcademicCalendarFromCourseCalendar,
  nonInstructionDatesByDate,
  toAWSDate,
} from "@examspark/common/dist/utils/coursePlanner";
import {
  NoResults,
  useMutationCreatePlannerItem,
  useMutationDeletePlannerItem,
  useMutationUpdatePlannerItem,
  useQueryGetClassroomCourseCalendar,
  useQueryGetUnit,
  useQueryGetUserClassrooms,
  useQueryListPlannerItems,
  useAppContext,
} from "@examspark/common-app";
import {
  Button,
  CautionBanner,
  ConfirmModal,
  Link,
  Modal,
  ModalProps,
  RouterLink,
  Text,
  TextArea,
  toast,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import {
  IconAddLine,
  IconPageNextLine,
  IconPagePreviousLine,
  IconPencilLine,
  IconTrashLine,
} from "@examspark/common-ui/dist/components/icons";
import { Resource, UpdateResourceInput, UserClassroom } from "@examspark/types";

import { getLinkForResource, getLinkForClassroomActivity } from "../../utils";
import SubjectIcon from "../common/SubjectIcon";

// use H4 styles but reset it to H2
const StyledClassroom = styled(Text.H4).attrs({ as: "h2" })`
  font-size: 1.4rem;
`;

const ClassroomItem = styled.div`
  border-top: solid 1px #ddd;
  padding: 1rem;

  &:first-child {
    border-top: none;
  }
`;

const NothingPlanned = styled.div`
  padding: 1rem 0;
`;

const PlannerListItem = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;

  min-height: 3rem;
`;

export const PlannerItem = (props: {
  item: Resource;
  classroomId: string | undefined;
  onDelete?: () => void;
}) => {
  const queryClient = useQueryClient();
  const deletePlannerItem = useMutationDeletePlannerItem();
  const externalLink = props.item.resource
    ? getLinkForResource(props.item.resource)
    : undefined;

  // handle internal links to an activity
  const linkToActivity = props.item.resource
    ? getLinkForClassroomActivity(props.item.resource)
    : undefined;

  // The previous logic in this component had some weird nesting that
  // was hard to understand. This isn't perfect, but it's a start.
  const [url, target]: [url: string | undefined, target: "_blank" | undefined] =
    props.item.resource?.name
      ? linkToActivity
        ? [linkToActivity, undefined]
        : externalLink
          ? [externalLink, "_blank"]
          : ["#", undefined]
      : [undefined, undefined];

  return (
    <PlannerListItem>
      {!!props.item.description && <span>{props.item.description}</span>}

      <div style={{ flex: 1 }}>
        {url && target === "_blank" && (
          <Link href={url} target={target}>
            {props.item.resource?.name}
          </Link>
        )}
        {url && target === undefined && (
          <RouterLink to={url}>{props.item.resource?.name}</RouterLink>
        )}

        {/* deleted/unavailable resource */}
        {props.item.resourceId && !props.item.resource && (
          <em>Deleted Resource</em>
        )}
      </div>
      <div>
        <Button
          title="Remove Item"
          variant="plain"
          icon={<IconTrashLine />}
          onClick={async () => {
            await ConfirmModal.show({
              onResolving: async (result) => {
                if (result) {
                  if (!props.item.workspaceId) {
                    toast.error("Error deleting planner item");
                    return;
                  }

                  try {
                    const res = await deletePlannerItem.mutateAsync({
                      id: props.item.id,
                      sk: props.item.sk,
                      workspaceId: props.item.workspaceId,
                    });

                    toast.success("Planner item deleted");

                    queryClient.setQueriesData(
                      ["classroom", props.classroomId, "planner-items"],
                      (old: Resource[] | undefined) => {
                        if (!old) return old;

                        return old.filter((r) => {
                          return !(
                            r.id === res.data?.deleteResource.id &&
                            r.sk === res.data?.deleteResource.sk
                          );
                        });
                      },
                    );
                    props.onDelete?.();
                  } catch (errors) {
                    console.error(errors);
                    toast.error("Error deleting planner item");
                  }
                }
              },
              children: (
                <div style={{ padding: "1rem" }}>
                  <CautionBanner variant="plain">
                    Are you sure you want to delete this planner item?
                  </CautionBanner>
                </div>
              ),
            });
          }}
        />
      </div>
    </PlannerListItem>
  );
};

const PlannerClassroomNoteItem = (props: {
  userClassroom: UserClassroom;
  date: Date;
  classroomNote: Resource | undefined;
}) => {
  const { classroomNote, date, userClassroom } = props;

  const [noteState, setNoteState] = React.useState<"view" | "edit">("view");
  const [noteValue, setNoteValue] = React.useState<string>("");
  const [noteIsSaving, setNoteIsSaving] = React.useState<boolean>(false);

  const deletePlannerItem = useMutationDeletePlannerItem();
  const createPlannerItem = useMutationCreatePlannerItem();
  const updatePlannerItem = useMutationUpdatePlannerItem();

  // create a new, blank note
  const onCreateNote = async () => {
    if (!userClassroom.classroom) {
      return;
    }

    setNoteIsSaving(true);

    const awsDate = toAWSDate(date);
    const newNote: Resource = {
      id: userClassroom.classroom.id,
      sk: `classroom-planner-item:${awsDate}`,
      schemaVersion: "2020-01-01",
      workspaceId: userClassroom.workspaceId,
      description: "",
    };

    try {
      const res = await createPlannerItem.mutateAsync(newNote);

      setNoteValue("");
      setNoteState("view");

      return res;
    } catch (errors) {
      console.error(errors);
      toast.error("Error creating planner item");
    } finally {
      setNoteIsSaving(false);
    }
  };

  const onUpdateNote = async () => {
    if (!classroomNote || !classroomNote.workspaceId) {
      return;
    }

    setNoteIsSaving(true);

    const updatedNote: UpdateResourceInput = {
      id: classroomNote.id,
      sk: classroomNote.sk,
      description: noteValue,
    };

    try {
      const res = await updatePlannerItem.mutateAsync(updatedNote);

      setNoteValue("");
      setNoteState("view");

      return res;
    } catch (errors) {
      console.error(errors);
      toast.error("Error updating planner item");
    } finally {
      setNoteIsSaving(false);
    }
  };

  const onRemoveNote = async () => {
    if (!classroomNote || !classroomNote.workspaceId) {
      return;
    }

    try {
      const res = await deletePlannerItem.mutateAsync({
        id: classroomNote.id,
        sk: classroomNote.sk,
        workspaceId: classroomNote.workspaceId,
      });

      setNoteValue("");
      setNoteState("view");

      return res;
    } catch (errors) {
      console.error(errors);
      toast.error("Error deleting planner item");
    }
  };

  if (classroomNote) {
    return (
      <PlannerListItem>
        {noteState === "edit" ? (
          <TextArea
            value={noteValue}
            onChange={(e) => {
              setNoteValue(e.target.value);
            }}
            style={{ marginBottom: "1rem" }}
          />
        ) : (
          <span style={{ whiteSpace: "pre-wrap" }}>
            {classroomNote.description}
          </span>
        )}
        <Toolbar.Right>
          {noteState === "edit" && (
            <>
              <Button
                title="Cancel"
                onClick={() => {
                  setNoteValue("");
                  setNoteState("view");
                }}
              >
                Cancel
              </Button>
              <Button
                title="Save Note"
                color="primary"
                disabled={noteIsSaving}
                onClick={async () => {
                  await onUpdateNote();
                }}
              >
                Save
              </Button>
            </>
          )}
          {noteState === "view" && (
            <>
              <Button
                title="Edit Note"
                onClick={() => {
                  setNoteValue(classroomNote.description ?? "");
                  setNoteState("edit");
                }}
                variant="plain"
              >
                <IconPencilLine width="12px" />
              </Button>
              <Button
                title="Delete Note"
                onClick={() => {
                  onRemoveNote();
                }}
                variant="plain"
              >
                <IconTrashLine width="12px" />
              </Button>
            </>
          )}
        </Toolbar.Right>
      </PlannerListItem>
    );
  } else {
    return (
      <PlannerListItem>
        <Button
          title="Add Note"
          color="create"
          disabled={noteIsSaving}
          onClick={async () => {
            await onCreateNote();
            setNoteState("edit");
          }}
          icon={<IconAddLine width="12px" />}
        >
          Note
        </Button>
      </PlannerListItem>
    );
  }
};

const PlannerClassroomItem = (props: {
  userClassroom: UserClassroom;
  date: Date;
  itemsForSelectedDate: Resource[];
}) => {
  const { userClassroom, itemsForSelectedDate } = props;

  const classroomItems = itemsForSelectedDate?.filter(
    (item) => item.id === userClassroom.id,
  );

  const awsDate = toAWSDate(props.date);

  const courseCalendarQuery = useQueryGetClassroomCourseCalendar(
    props.userClassroom.classroom ?? undefined,
  );

  const academicCalendar = useMemo(() => {
    if (!courseCalendarQuery.data) return;
    return getFullAcademicCalendarFromCourseCalendar(courseCalendarQuery.data);
  }, [courseCalendarQuery.data]);

  const units = courseCalendarQuery.data?.units;
  const unitPlanForDate = units?.find((unit) =>
    unit.dates?.find((d) => d === awsDate),
  );
  const unitQuery = useQueryGetUnit(unitPlanForDate?.id ?? undefined);

  const unitInProgressAtDate = unitQuery.data;

  const holidays = useMemo(
    () => nonInstructionDatesByDate(academicCalendar?.nonInstructionDates),
    [academicCalendar],
  );

  const informationals: any[] = [];

  if (holidays?.[awsDate]) {
    informationals.push(holidays[awsDate].label);
  }

  const gradingPeriodMatchStart = academicCalendar?.gradingPeriods?.find(
    (gp) => gp.startAt === awsDate,
  );

  const gradingPeriodMatchEnd = academicCalendar?.gradingPeriods?.find(
    (gp) => gp.endAt === awsDate,
  );

  if (gradingPeriodMatchStart) {
    informationals.push(`${gradingPeriodMatchStart.label} begins`);
  }

  if (gradingPeriodMatchEnd) {
    informationals.push(`${gradingPeriodMatchEnd.label} ends`);
  }

  // filter out deleted/invalid classrooms
  if (!userClassroom.classroom) {
    return null;
  }

  // pull out the note planner item if it exists
  const classroomResources = classroomItems.filter(
    (item) => item.sk !== `classroom-planner-item:${awsDate}`,
  );
  const classroomNote = classroomItems.find(
    (item) => item.sk === `classroom-planner-item:${awsDate}`,
  );

  return (
    <ClassroomItem key={userClassroom.id}>
      <VSpace>
        <div style={{ display: "flex", flexDirection: "row", gap: "2ch" }}>
          <SubjectIcon
            value={userClassroom.classroom?.subject?.at(0) ?? undefined}
          />
          <VSpace size="0.5rem">
            <StyledClassroom>
              <span>{userClassroom.classroom?.name}</span>
            </StyledClassroom>
            <div>
              {userClassroom.classroom.course?.name && (
                <span>{userClassroom.classroom.course.name}</span>
              )}
              {unitInProgressAtDate?.name && (
                <span> | {unitInProgressAtDate.name}</span>
              )}
            </div>
          </VSpace>
        </div>
        <div>
          {informationals.map((info) => (
            <PlannerListItem key={info}>{info}</PlannerListItem>
          ))}
          {!classroomItems?.length && (
            <NothingPlanned>Nothing planned.</NothingPlanned>
          )}
          {!!classroomItems.length && (
            <>
              {classroomResources
                .sort(
                  (a, b) =>
                    a.resource?.name?.localeCompare(b.resource?.name ?? "") ??
                    0,
                )
                .map((item) => (
                  <PlannerItem
                    key={reactKeyFromIDSK(item)}
                    item={item}
                    classroomId={userClassroom.classroom?.id}
                  />
                ))}
            </>
          )}
          <PlannerClassroomNoteItem
            userClassroom={userClassroom}
            date={props.date}
            classroomNote={classroomNote}
          />
        </div>
      </VSpace>
    </ClassroomItem>
  );
};

const PlannerDayViewModal = ({
  selectedDate,
  setSelectedDate,
  ...props
}: ModalProps & {
  selectedDate?: Date;
  setSelectedDate?: (date: Date) => void;
}) => {
  const [state] = useAppContext();
  const { data: classrooms } = useQueryGetUserClassrooms(
    state.workspace?.id,
    state.user?.id,
  );

  const awsDate = useMemo(
    () => (selectedDate ? toAWSDate(selectedDate) : undefined),
    [selectedDate],
  );

  // we get a month's worth of planner items because it should already be cached from the calendar view
  // whereas only grabbing the dates' worth of planner items would require a new query
  const monthString = awsDate?.slice(0, 7);
  const { data: plannerItems } = useQueryListPlannerItems(monthString);

  const itemsForSelectedDate =
    plannerItems?.filter((item) => {
      const [, date] = item.sk.split(":");
      return date === awsDate;
    }) ?? [];

  const modalTitle = selectedDate?.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  if (!selectedDate) return null;

  const realClassrooms = classrooms?.filter((uc) => uc.classroom) ?? [];

  return (
    <Modal
      title={modalTitle}
      size="medium"
      titlebarToolbar={
        <>
          {setSelectedDate && (
            <Toolbar>
              <Button
                title="Previous Day"
                variant="plain"
                onClick={() => {
                  setSelectedDate(
                    new Date(selectedDate?.setDate(selectedDate.getDate() - 1)),
                  );
                }}
              >
                <IconPagePreviousLine width={16} />
              </Button>
              <Button
                title="Next Day"
                variant="plain"
                onClick={() => {
                  setSelectedDate(
                    new Date(selectedDate?.setDate(selectedDate.getDate() + 1)),
                  );
                }}
              >
                <IconPageNextLine width={16} />
              </Button>
            </Toolbar>
          )}
        </>
      }
      {...props}
    >
      <div>
        {realClassrooms.length === 0 && (
          <>
            <NoResults>
              <Text.Body>
                You must have at least one classroom to use the calendar day
                view.
              </Text.Body>
            </NoResults>
          </>
        )}
        {realClassrooms.map((userClassroom) => (
          <PlannerClassroomItem
            key={userClassroom.classroom?.id}
            date={selectedDate}
            userClassroom={userClassroom}
            itemsForSelectedDate={itemsForSelectedDate}
          />
        ))}
      </div>
    </Modal>
  );
};

export default PlannerDayViewModal;
